/**
 * Type definitions for Portal-based deployment system
 */

import { z } from 'zod/v3';

/**
 * Portal deployment options schema
 */
export const PortalDeploymentOptionsSchema = z.object({
  branchIds: z.record(z.string()).optional().describe('Mapping of workspaceId to branchId'),
  commitMessage: z.string().optional(),
  forceSync: z.boolean().optional(),
});

export type PortalDeploymentOptions = z.infer<typeof PortalDeploymentOptionsSchema>;

/**
 * Workspace content for Portal deployment
 */
export const WorkspaceContentSchema = z.object({
  workspaceId: z.string(),
  workspaceSlug: z.string(),
  displayName: z.string(),
  order: z.number(),
  isFeatured: z.boolean(),
  sourceDir: z.string(),
  branchId: z.string(),
  commitSha: z.string().optional(),
});

export type WorkspaceContent = z.infer<typeof WorkspaceContentSchema>;

/**
 * Workspace metadata for Portal
 */
export const WorkspaceMetadataSchema = z.object({
  workspaceId: z.string(),
  workspaceSlug: z.string(),
  displayName: z.string(),
  order: z.number(),
  isFeatured: z.boolean(),
  metadata: z.any(), // The workspace's metadata.json content
});

export type WorkspaceMetadata = z.infer<typeof WorkspaceMetadataSchema>;

/**
 * Portal site metadata
 */
export const PortalSiteMetadataSchema = z.object({
  site: z.object({
    title: z.string(),
    description: z.string(),
    version: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    portalId: z.string(),
    portalSlug: z.string(),
    customDomain: z.string().nullable().optional(),
    organizationId: z.string(),
  }),
  workspaces: z.array(WorkspaceMetadataSchema),
  navigation: z.object({
    topicHierarchy: z.array(z.any()), // Using any for now, should be TopicHierarchySchema
  }),
  formatOptions: z.object({
    theme: z.object({
      primaryColor: z.string().optional(),
      logo: z.string().optional(),
      favicon: z.string().optional(),
    }).optional(),
  }).optional(),
});

export type PortalSiteMetadata = z.infer<typeof PortalSiteMetadataSchema>;

/**
 * Portal deployment request DTO
 */
export const DeployPortalRequestSchema = z.object({
  portalId: z.string(),
  branchIds: z.record(z.string()).optional(),
  commitMessage: z.string().optional(),
});

export type DeployPortalRequest = z.infer<typeof DeployPortalRequestSchema>;

/**
 * Portal deployments query DTO
 */
export const GetPortalDeploymentsRequestSchema = z.object({
  portalId: z.string(),
  limit: z.number().min(1).max(100).default(20),
  offset: z.number().min(0).default(0),
});

export type GetPortalDeploymentsRequest = z.infer<typeof GetPortalDeploymentsRequestSchema>;

/**
 * Portal deployment response DTO
 */
export const PortalDeploymentResponseSchema = z.object({
  deployments: z.array(z.object({
    id: z.string(),
    portalId: z.string().nullable(),
    domain: z.string().nullable(),
    status: z.string(),
    createdAt: z.date(),
    completedAt: z.date().nullable(),
    errorMessage: z.string().nullable(),
    createdByUser: z.object({
      id: z.string(),
      name: z.string().nullable(),
      email: z.string().nullable(),
      image: z.string().nullable(),
    }).nullable(),
    export: z.object({
      id: z.string(),
      commitSha: z.string().nullable(),
      createdAt: z.date(),
      updatedAt: z.date(),
    }).nullable(),
    site: z.object({
      id: z.string(),
      domain: z.string(),
      screenshotUrl: z.string().nullable(),
    }).nullable(),
  })),
  totalCount: z.number(),
  limit: z.number(),
  offset: z.number(),
  hasMore: z.boolean(),
  totalPages: z.number(),
});

export type PortalDeploymentResponse = z.infer<typeof PortalDeploymentResponseSchema>;